import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, auth } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Upload, Check } from "lucide-react";
import TutorProfileSection from "@/components/TutorProfileSection";
import { TUTOR_SERVICE_ID } from "@/lib/tutorConfig";

export default function PartnerRegister() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mobile = params.get("mobile") || "";
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "",
    address: "",
    aadhar_number: "",
    aadhar_file: "",
    services: [],
    service_pincode: "",
    service_radius_km: 10,
    service_location: "",
    tutor_profile: { classes: [], subjects: [] },
  });
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  if (!mobile) {
    navigate("/partner/login");
    return null;
  }

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id)
        ? f.services.filter((s) => s !== id)
        : [...f.services, id],
    }));
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, aadhar_file: r.data.filename }));
      toast.success("Aadhar uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.aadhar_number || form.services.length === 0 || !form.service_pincode) {
      return toast.error("Please fill all required fields and pick at least one service");
    }
    const isTutor = form.services.includes(TUTOR_SERVICE_ID);
    if (isTutor && form.tutor_profile.classes.length === 0) {
      return toast.error("As a Home Tutor, please pick at least one class you teach");
    }
    setLoading(true);
    try {
      const payload = { mobile, ...form, tutor_profile: isTutor ? form.tutor_profile : null };
      const r = await api.post("/partners/register", payload);
      auth.setSession(r.data.token, "partner", r.data.user);
      toast.success("Welcome aboard!");
      navigate("/partner/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar variant="dashboard" />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 fade-in">
        <div className="text-xs uppercase tracking-widest text-[#E06D53] font-semibold">
          Partner Registration
        </div>
        <h1 className="font-display text-4xl mt-3">Tell us about your business</h1>
        <p className="text-[#4A4A4A] mt-3">
          Mobile: <strong>{mobile}</strong>
        </p>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="partner-register-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Full name</label>
              <input
                className="octa-input mt-2"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="partner-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Aadhar number</label>
              <input
                className="octa-input mt-2"
                value={form.aadhar_number}
                onChange={(e) => setForm({ ...form, aadhar_number: e.target.value.replace(/\D/g, "").slice(0, 12) })}
                placeholder="12-digit"
                data-testid="partner-aadhar-input"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Address</label>
            <textarea
              className="octa-input mt-2"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="partner-address-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Upload Aadhar</label>
            <label className="mt-2 flex items-center gap-3 octa-input cursor-pointer" data-testid="partner-aadhar-upload-label">
              <Upload className="w-4 h-4 text-[#737373]" />
              <span className="text-sm text-[#737373]">
                {form.aadhar_file ? (
                  <span className="text-[#1E3F20] inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Uploaded</span>
                ) : uploading ? "Uploading..." : "Click to upload Aadhar (JPG/PNG/PDF)"}
              </span>
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                data-testid="partner-aadhar-file-input"
              />
            </label>
          </div>

          <div>
            <label className="text-sm font-medium">Services you provide</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
              {services.map((s) => {
                const active = form.services.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggleService(s.id)}
                    className={`p-3 rounded-xl text-sm font-medium text-left border transition-colors ${active ? "bg-[#1E3F20] text-white border-[#1E3F20]" : "bg-white border-[#E5E3DB] hover:border-[#1E3F20]"}`}
                    data-testid={`partner-service-toggle-${s.id}`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Service pincode</label>
              <input
                className="octa-input mt-2"
                value={form.service_pincode}
                onChange={(e) => setForm({ ...form, service_pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                data-testid="partner-pincode-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Service location</label>
              <input
                className="octa-input mt-2"
                value={form.service_location}
                onChange={(e) => setForm({ ...form, service_location: e.target.value })}
                placeholder="City / area"
                data-testid="partner-location-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Service radius: {form.service_radius_km} km</label>
              <input
                type="range"
                min="2"
                max="50"
                value={form.service_radius_km}
                onChange={(e) => setForm({ ...form, service_radius_km: parseInt(e.target.value) })}
                className="w-full mt-4"
                data-testid="partner-radius-input"
              />
            </div>
          </div>

          {form.services.includes(TUTOR_SERVICE_ID) && (
            <TutorProfileSection
              value={form.tutor_profile}
              onChange={(tp) => setForm({ ...form, tutor_profile: tp })}
            />
          )}

          <button
            type="submit"
            disabled={loading}
            className="h-12 px-8 rounded-full btn-primary-octa font-semibold disabled:opacity-50"
            data-testid="partner-register-submit"
          >
            {loading ? "Creating account..." : "Complete Registration"}
          </button>
          <p className="text-xs text-[#737373]">
            You can add optional KYC (PAN, GST, Trade Licence) later in Account Settings.
          </p>
        </form>
      </div>
    </div>
  );
}
